/**
 * Bulk Sync Modal
 * UI for batch syncing unsynced accounts by platform
 */

import React, { useState, useMemo } from 'react';
import { X, Play, Square, CheckCircle, XCircle, Clock, Users, Calendar, Loader2, AlertTriangle } from 'lucide-react';
import { useBulkSync, BulkSyncAccount } from '../../hooks/useBulkSync';

type SyncMode = 'full' | 'players_only';

interface BulkSyncModalProps {
  isOpen: boolean;
  onClose: () => void;
  networks: Array<{
    id: string;
    account_name: string;
    platform_name: string | null;
    platform_provider: string | null; // The affiliate system (NetRefer, MyAffiliates, etc.)
    last_sync_at: string | null;
  }>;
}

export function BulkSyncModal({ isOpen, onClose, networks }: BulkSyncModalProps) {
  const [selectedPlatform, setSelectedPlatform] = useState<string>('');
  const [syncAllAccounts, setSyncAllAccounts] = useState<boolean>(false);
  const [syncMode, setSyncMode] = useState<SyncMode>('full');
  const { progress, startBulkSync, startBulkPlayerSync, stopBulkSync, resetProgress } = useBulkSync();

  // Dynamically generate platform options from networks data
  const platformOptions = useMemo(() => {
    const platforms = new Map<string, string>();
    
    networks.forEach(n => {
      const provider = n.platform_provider;
      if (provider && provider !== 'Unknown') {
        // Use lowercase key for deduplication, original value for display
        const key = provider.toLowerCase().trim();
        if (!platforms.has(key)) {
          platforms.set(key, provider);
        }
      }
    });
    
    // Sort alphabetically and return as options array
    return Array.from(platforms.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([key, label]) => ({ value: label, label }));
  }, [networks]);

  // Filter accounts for selected platform (uses platform_provider for affiliate system matching)
  const filteredAccounts = useMemo(() => {
    if (!selectedPlatform) return [];
    
    return networks
      .filter(n => {
        // Match by platform_provider (affiliate system like NetRefer, MyAffiliates, etc.)
        const platformMatch = n.platform_provider?.toLowerCase() === selectedPlatform.toLowerCase();
        if (!platformMatch) return false;
        
        // If syncAllAccounts is true, include all; otherwise only unsynced
        if (syncAllAccounts) return true;
        
        const isUnsynced = !n.last_sync_at || n.last_sync_at === 'Never';
        return isUnsynced;
      })
      .map(n => ({
        id: n.id,
        name: n.account_name,
        platform: n.platform_provider || '',
      }));
  }, [networks, selectedPlatform, syncAllAccounts]);

  // For backwards compatibility
  const unsyncedAccounts = filteredAccounts;

  // Count by platform provider (affiliate system)
  const platformCounts = useMemo(() => {
    const counts: Record<string, { total: number; unsynced: number }> = {};
    
    networks.forEach(n => {
      const platform = n.platform_provider || 'Unknown';
      if (!counts[platform]) {
        counts[platform] = { total: 0, unsynced: 0 };
      }
      counts[platform].total++;
      if (!n.last_sync_at || n.last_sync_at === 'Never') {
        counts[platform].unsynced++;
      }
    });
    
    return counts;
  }, [networks]);

  const handleStartSync = () => {
    if (filteredAccounts.length > 0) {
      if (syncMode === 'players_only') {
        startBulkPlayerSync(filteredAccounts);
      } else {
        startBulkSync(filteredAccounts);
      }
    }
  };

  const handleClose = () => {
    if (!progress.isRunning) {
      resetProgress();
      onClose();
    }
  };

  if (!isOpen) return null;

  const getPhaseLabel = (phase: string) => {
    switch (phase) {
      case 'fetching_history': return 'Fetching History...';
      case 'syncing_players': return 'Syncing Players...';
      case 'waiting': return 'Cooldown...';
      case 'complete': return 'Complete!';
      case 'error': return 'Error';
      default: return 'Ready';
    }
  };

  const getPhaseColor = (phase: string) => {
    switch (phase) {
      case 'fetching_history': return 'text-blue-400';
      case 'syncing_players': return 'text-purple-400';
      case 'waiting': return 'text-yellow-400';
      case 'complete': return 'text-emerald-400';
      case 'error': return 'text-red-400';
      default: return 'text-zinc-400';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-[hsl(225,15%,12%)] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-[hsl(225,15%,20%)]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[hsl(225,15%,20%)]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
              <Play className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">Bulk Sync</h2>
              <p className="text-sm text-zinc-400">Sync all unsynced accounts by platform</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={progress.isRunning}
            className="p-2 text-zinc-400 hover:text-white hover:bg-[hsl(225,15%,18%)] rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 overflow-y-auto max-h-[60vh]">
          {/* Platform Selection */}
          {!progress.isRunning && progress.currentPhase !== 'complete' && (
            <div className="space-y-4">
              <label className="block text-sm font-medium text-zinc-300">
                Select Platform
              </label>
              <select
                value={selectedPlatform}
                onChange={(e) => setSelectedPlatform(e.target.value)}
                className="w-full px-4 py-3 bg-[hsl(225,15%,15%)] border border-[hsl(225,15%,25%)] rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Choose a platform...</option>
                {platformOptions.map(opt => {
                  const count = platformCounts[opt.value];
                  return (
                    <option key={opt.value} value={opt.value}>
                      {opt.label} {count ? `(${count.unsynced} unsynced / ${count.total} total)` : ''}
                    </option>
                  );
                })}
              </select>

              {/* Sync Mode Selection */}
              {selectedPlatform && (
                <div className="mt-4 space-y-3">
                  <label className="block text-sm font-medium text-zinc-300">
                    Sync Mode
                  </label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setSyncMode('full')}
                      className={`flex-1 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                        syncMode === 'full'
                          ? 'bg-blue-600/20 border-blue-500 text-blue-400'
                          : 'bg-[hsl(225,15%,15%)] border-[hsl(225,15%,25%)] text-zinc-400 hover:border-zinc-500'
                      }`}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>Full Sync</span>
                      </div>
                      <p className="text-xs mt-1 opacity-70">Statistics + Players</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setSyncMode('players_only')}
                      className={`flex-1 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                        syncMode === 'players_only'
                          ? 'bg-purple-600/20 border-purple-500 text-purple-400'
                          : 'bg-[hsl(225,15%,15%)] border-[hsl(225,15%,25%)] text-zinc-400 hover:border-zinc-500'
                      }`}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <Users className="w-4 h-4" />
                        <span>Players Only</span>
                      </div>
                      <p className="text-xs mt-1 opacity-70">Faster, just players</p>
                    </button>
                  </div>
                </div>
              )}

              {/* Sync All Toggle */}
              {selectedPlatform && (
                <div className="mt-4 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setSyncAllAccounts(!syncAllAccounts)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      syncAllAccounts ? 'bg-blue-600' : 'bg-[hsl(225,15%,25%)]'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        syncAllAccounts ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <label className="text-sm text-zinc-300">
                    {syncAllAccounts ? 'Sync ALL accounts (including already synced)' : 'Sync only unsynced accounts'}
                  </label>
                </div>
              )}

              {/* Account Preview */}
              {selectedPlatform && (
                <div className="mt-4 p-4 bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)]">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-zinc-300">Accounts to Sync</h3>
                    <span className="text-xs text-zinc-500">{unsyncedAccounts.length} accounts</span>
                  </div>
                  
                  {unsyncedAccounts.length === 0 ? (
                    <p className="text-sm text-zinc-500 italic">No unsynced accounts found for this platform</p>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {unsyncedAccounts.map(acc => (
                        <div key={acc.id} className="flex items-center gap-2 text-sm">
                          <div className="w-2 h-2 rounded-full bg-zinc-500" />
                          <span className="text-zinc-300">{acc.name}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Time Estimate */}
                  {filteredAccounts.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-[hsl(225,15%,18%)] flex items-center gap-2 text-sm text-zinc-400">
                      <Clock className="w-4 h-4" />
                      <span>
                        Estimated time: ~{syncMode === 'players_only' 
                          ? Math.ceil(filteredAccounts.length * 0.5) 
                          : Math.ceil(filteredAccounts.length * 2.5)} minutes
                        {syncMode === 'players_only' && <span className="text-purple-400 ml-1">(faster)</span>}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Progress Display */}
          {(progress.isRunning || progress.currentPhase === 'complete') && (
            <div className="space-y-6">
              {/* Current Status */}
              <div className="p-4 bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)]">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {progress.isRunning ? (
                      <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
                    ) : progress.currentPhase === 'complete' ? (
                      <CheckCircle className="w-6 h-6 text-emerald-400" />
                    ) : (
                      <AlertTriangle className="w-6 h-6 text-yellow-400" />
                    )}
                    <div>
                      <p className={`font-medium ${getPhaseColor(progress.currentPhase)}`}>
                        {getPhaseLabel(progress.currentPhase)}
                      </p>
                      {progress.isRunning && (
                        <p className="text-sm text-zinc-400">
                          {progress.currentAccountName} ({progress.currentAccountIndex + 1}/{progress.totalAccounts})
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {progress.estimatedTimeRemaining && (
                    <div className="text-right">
                      <p className="text-xs text-zinc-500">ETA</p>
                      <p className="text-sm text-zinc-300">{progress.estimatedTimeRemaining}</p>
                    </div>
                  )}
                </div>

                {/* Progress Bar */}
                <div className="h-2 bg-[hsl(225,15%,20%)] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-500"
                    style={{ width: `${((progress.currentAccountIndex + (progress.isRunning ? 0.5 : 1)) / progress.totalAccounts) * 100}%` }}
                  />
                </div>

                {/* Iteration Counter */}
                {progress.isRunning && progress.currentIteration > 0 && (
                  <p className="mt-2 text-xs text-zinc-500 text-center">
                    Iteration {progress.currentIteration}
                  </p>
                )}
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] text-center">
                  <Calendar className="w-5 h-5 text-blue-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{progress.monthsSynced}</p>
                  <p className="text-xs text-zinc-500">Months Synced</p>
                </div>
                <div className="p-4 bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] text-center">
                  <Users className="w-5 h-5 text-purple-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{progress.playersSynced}</p>
                  <p className="text-xs text-zinc-500">Players Synced</p>
                </div>
                <div className="p-4 bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] text-center">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{progress.completedAccounts.length}</p>
                  <p className="text-xs text-zinc-500">Completed</p>
                </div>
              </div>

              {/* Failed Accounts */}
              {progress.failedAccounts.length > 0 && (
                <div className="p-4 bg-red-900/20 rounded-xl border border-red-800/30">
                  <div className="flex items-center gap-2 mb-3">
                    <XCircle className="w-5 h-5 text-red-400" />
                    <h4 className="font-medium text-red-400">Failed Accounts</h4>
                  </div>
                  <div className="space-y-2">
                    {progress.failedAccounts.map(acc => (
                      <div key={acc.id} className="text-sm">
                        <span className="text-red-300">{acc.name}</span>
                        <span className="text-red-500 ml-2">— {acc.error}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Completed Accounts List */}
              {progress.completedAccounts.length > 0 && (
                <div className="p-4 bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)]">
                  <h4 className="text-sm font-medium text-zinc-300 mb-3">Completed</h4>
                  <div className="flex flex-wrap gap-2">
                    {progress.completedAccounts.map(id => {
                      const acc = unsyncedAccounts.find(a => a.id === id);
                      return (
                        <span
                          key={id}
                          className="px-2 py-1 text-xs bg-emerald-900/30 text-emerald-400 rounded-lg border border-emerald-800/30"
                        >
                          {acc?.name || id}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[hsl(225,15%,20%)] bg-[hsl(225,15%,10%)]">
          {!progress.isRunning && progress.currentPhase !== 'complete' && (
            <>
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-zinc-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleStartSync}
                disabled={filteredAccounts.length === 0}
                className={`inline-flex items-center gap-2 px-6 py-2.5 text-white rounded-xl font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                  syncMode === 'players_only'
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500'
                }`}
              >
                {syncMode === 'players_only' ? <Users className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                {syncMode === 'players_only' ? 'Sync Players' : (syncAllAccounts ? 'Resync' : 'Sync')} {filteredAccounts.length} Account{filteredAccounts.length !== 1 ? 's' : ''}
              </button>
            </>
          )}

          {progress.isRunning && (
            <button
              onClick={stopBulkSync}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-red-600 text-white rounded-xl font-medium hover:bg-red-500 transition-all"
            >
              <Square className="w-4 h-4" />
              Stop Sync
            </button>
          )}

          {progress.currentPhase === 'complete' && (
            <button
              onClick={handleClose}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-500 transition-all"
            >
              <CheckCircle className="w-4 h-4" />
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
